/**
 * c-home-waves.js — live-drawn wave graphics for the 2026 homepage (ClickUp 123y1w8fdpr).
 *
 * Sections 04 (findings), 06 (breath / touch / sleep) and 08 (the day wave) share this
 * renderer. The designer's annotations ask for the waves to be "drawn live in the browser,
 * so they stay sharp at any size and add almost nothing to page weight".
 *
 * A graphic is an <svg data-c-wave> holding a JSON spec in a child
 * <script type="application/json">:
 *
 *   {
 *     "width": 584, "height": 124,          // the design box; the SVG stretches to its CSS size
 *     "lines": [{
 *       "points": [[x, y], ...],             // 0..1 in the box, left to right
 *       "stops": [["#9c180c", 0], ...],      // horizontal gradient, [colour, offset 0..1]
 *       "weight": 5, "opacity": 1,
 *       "wash": [["#9c180c", 0.24], ["#9c180c", 0]],  // optional fill under the line, top -> bottom
 *       "speed": 0.075, "amp": 0.045,        // drift: numbers, or arrays per point
 *       "draw": 1200, "delay": 0             // draw-on, ms
 *     }],
 *     // optional "glows" on a line: light running along it (the design team's motion reference,
 *     // "Apollo Homepage in Motion"). dur in seconds, width in px, dash in % of the line, class for a
 *     // section-defined animation (e.g. the day wave's uneven pace), delay = fade-in delay in seconds.
 *     //   "glows": [{"color": "#BFE9E4", "dur": 2.6, "width": 7, "dash": 14, "opacity": 1}]
 *     "ticks": { "count": 6, "color": "#16101f", "opacity": 1 },   // optional
 *     "start": "visible" | "manual"          // "manual": wait for element.cWave.start()
 *   }
 *
 * Drift: each control point moves as y + amp * sin(2π · RATE · speed · t + 2π · 1.5 · x).
 * The phase follows x, so the curve travels across the box and reshapes as it goes instead of
 * visibly looping. RATE only sets the absolute tempo; the ratios between the speeds in the
 * specs are the design, and they are kept as given.
 *
 * Reduced motion: every line is drawn in its resting shape, with no draw-on and no drift.
 * Off screen: the animation frame loop stops.
 */
(function () {
  if (window.CHomeWaves) return;

  var SVG = 'http://www.w3.org/2000/svg';
  var RATE = 4;
  var TRAVEL = 1.5 * Math.PI * 2;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var uid = 0;

  function el(name, attrs, parent) {
    var node = document.createElementNS(SVG, name);
    for (var key in attrs) node.setAttribute(key, attrs[key]);
    if (parent) parent.appendChild(node);
    return node;
  }

  // Glow styles, injected once. The dash runs 114 -> 0 over pathLength 100, so a light enters at the start
  // of the line and leaves at the end. Paused while the graphic is off screen.
  function injectStyles() {
    if (document.getElementById('c-home-waves-css')) return;
    var style = document.createElement('style');
    style.id = 'c-home-waves-css';
    style.textContent =
      '.c-wave-glow{fill:none;stroke:var(--c);stroke-width:var(--w);stroke-linecap:round;' +
      'stroke-dasharray:var(--dash) 100;stroke-dashoffset:114;filter:drop-shadow(0 0 6px var(--c));' +
      'opacity:0;transition:opacity .7s ease-out var(--fade,0s);' +
      'animation:c-wave-run var(--dur) linear infinite;animation-play-state:paused}' +
      '.c-wave-glow--swell{animation:c-wave-run var(--dur) linear infinite,c-wave-swell 7.2s ease-in-out infinite;' +
      'animation-play-state:paused}' +
      '[data-c-wave].is-live .c-wave-glow{opacity:var(--o);animation-play-state:running}' +
      '@keyframes c-wave-run{from{stroke-dashoffset:114}to{stroke-dashoffset:0}}' +
      '@keyframes c-wave-swell{0%,100%{opacity:.4;stroke-width:4px}50%{opacity:1;stroke-width:9px}}' +
      '@media (prefers-reduced-motion:reduce){.c-wave-glow,.c-wave-glow--swell{animation:none;' +
      'stroke-dasharray:none;opacity:.45 !important;transition:none}}';
    document.head.appendChild(style);
  }

  function perPoint(value, i) {
    return Array.isArray(value) ? value[i] || 0 : value || 0;
  }

  /** Uniform Catmull-Rom through the points, as cubic Bézier segments. */
  function curve(points) {
    var d = 'M' + points[0][0].toFixed(2) + ' ' + points[0][1].toFixed(2);
    for (var i = 0; i < points.length - 1; i++) {
      var p0 = points[i - 1] || points[i];
      var p1 = points[i];
      var p2 = points[i + 1];
      var p3 = points[i + 2] || p2;
      d += ' C' + (p1[0] + (p2[0] - p0[0]) / 6).toFixed(2) + ' ' + (p1[1] + (p2[1] - p0[1]) / 6).toFixed(2) +
        ' ' + (p2[0] - (p3[0] - p1[0]) / 6).toFixed(2) + ' ' + (p2[1] - (p3[1] - p1[1]) / 6).toFixed(2) +
        ' ' + p2[0].toFixed(2) + ' ' + p2[1].toFixed(2);
    }
    return d;
  }

  function easeOut(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function Wave(svg) {
    var source = svg.querySelector('script[type="application/json"]');
    this.spec = JSON.parse(source.textContent);
    this.svg = svg;
    this.lines = [];
    this.listeners = [];
    this.started = false;
    this.running = false;
    this.visible = false;
    this.t0 = 0;
    this.build();
    svg.cWave = this;
  }

  Wave.prototype.build = function () {
    var spec = this.spec;
    var W = spec.width;
    var H = spec.height;
    var svg = this.svg;
    var id = 'cw' + ++uid;
    svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var defs = el('defs', {}, svg);

    if (spec.ticks) {
      var n = spec.ticks.count;
      for (var k = 0; k < n; k++) {
        var x = ((k + 0.5) / n) * W;
        el('line', {
          x1: x, x2: x, y1: 0, y2: H,
          stroke: spec.ticks.color, 'stroke-opacity': spec.ticks.opacity == null ? 1 : spec.ticks.opacity,
          'stroke-width': 1, 'vector-effect': 'non-scaling-stroke'
        }, svg);
      }
    }

    // Every wash sits under every stroke: "wash + secondary stroke + primary stroke, in that order".
    var washLayer = el('g', {}, svg);
    var strokeLayer = el('g', {}, svg);
    var glowLayer = el('g', {}, svg);
    var self = this;
    spec.lines.forEach(function (line, index) {
      var gid = id + 'g' + index;
      var grad = el('linearGradient', { id: gid, gradientUnits: 'userSpaceOnUse', x1: 0, y1: 0, x2: W, y2: 0 }, defs);
      line.stops.forEach(function (stop) {
        el('stop', { offset: stop[1], 'stop-color': stop[0] }, grad);
      });
      var clipId = id + 'c' + index;
      var clip = el('clipPath', { id: clipId }, defs);
      var clipRect = el('rect', { x: 0, y: -H, width: W, height: H * 3 }, clip);
      var group = el('g', { 'clip-path': 'url(#' + clipId + ')' }, strokeLayer);

      var washPath = null;
      if (line.wash) {
        var wid = id + 'w' + index;
        var washGrad = el('linearGradient', { id: wid, x1: 0, y1: 0, x2: 0, y2: 1 }, defs);
        line.wash.forEach(function (stop, s) {
          el('stop', {
            offset: stop[2] == null ? s / (line.wash.length - 1) : stop[2],
            'stop-color': stop[0], 'stop-opacity': stop[1]
          }, washGrad);
        });
        washPath = el('path', { fill: 'url(#' + wid + ')', 'clip-path': 'url(#' + clipId + ')' }, washLayer);
      }
      var path = el('path', {
        fill: 'none', stroke: 'url(#' + gid + ')', 'stroke-width': line.weight,
        'stroke-linecap': 'round', 'stroke-linejoin': 'round',
        'stroke-opacity': line.opacity == null ? 1 : line.opacity,
        'vector-effect': 'non-scaling-stroke'
      }, group);

      var glows = (line.glows || []).map(function (glow) {
        var node = el('path', {
          'class': 'c-wave-glow' + (glow.swell ? ' c-wave-glow--swell' : '') + (glow['class'] ? ' ' + glow['class'] : ''),
          pathLength: 100,
          'clip-path': 'url(#' + clipId + ')',
          'vector-effect': 'non-scaling-stroke'
        }, glowLayer);
        node.style.setProperty('--c', glow.color);
        node.style.setProperty('--dur', (glow.dur || 2.6) + 's');
        node.style.setProperty('--w', (glow.width || 7) + 'px');
        node.style.setProperty('--dash', glow.dash || 14);
        node.style.setProperty('--o', glow.opacity == null ? 1 : glow.opacity);
        node.style.setProperty('--fade', (glow.delay || 0) + 's');
        return node;
      });
      if (glows.length) injectStyles();

      self.lines.push({ spec: line, path: path, wash: washPath, clipRect: clipRect, glows: glows });
    });

    this.render(0, reduceMotion.matches ? 1 : 0);
  };

  /** Draws every line at time t (seconds of drift), revealed up to `progress` of its draw-on. */
  Wave.prototype.render = function (t, progress, elapsed) {
    var W = this.spec.width;
    var H = this.spec.height;
    this.lines.forEach(function (line) {
      var spec = line.spec;
      var pts = spec.points.map(function (p, i) {
        var y = p[1];
        var amp = perPoint(spec.amp, i);
        if (amp && t) y += amp * Math.sin(Math.PI * 2 * RATE * perPoint(spec.speed, i) * t + TRAVEL * p[0]);
        return [p[0] * W, y * H];
      });
      var d = curve(pts);
      line.path.setAttribute('d', d);
      for (var g = 0; g < line.glows.length; g++) line.glows[g].setAttribute('d', d);
      if (line.wash) line.wash.setAttribute('d', d + ' L' + W + ' ' + H + ' L0 ' + H + ' Z');

      var reveal = 1;
      if (elapsed != null) {
        var local = (elapsed - (spec.delay || 0)) / (spec.draw || 1);
        reveal = easeOut(Math.max(0, Math.min(1, local)));
      } else if (progress != null) {
        reveal = progress;
      }
      line.clipRect.setAttribute('width', (W * reveal).toFixed(2));
      line.reveal = reveal;
    });
  };

  /** The furthest draw-on head across the lines, 0..1: sections use it to light labels. */
  Wave.prototype.head = function () {
    var head = this.lines.length ? 0 : 1;
    for (var i = 0; i < this.lines.length; i++) head = Math.max(head, this.lines[i].reveal || 0);
    return head;
  };

  Wave.prototype.onFrame = function (fn) {
    this.listeners.push(fn);
  };

  Wave.prototype.start = function () {
    if (this.started) return;
    this.started = true;
    this.svg.classList.add('is-live');
    if (reduceMotion.matches) {
      this.render(0, 1);
      this.emit();
      return;
    }
    this.t0 = performance.now();
    this.resume();
  };

  Wave.prototype.emit = function () {
    for (var i = 0; i < this.listeners.length; i++) this.listeners[i](this.head());
  };

  Wave.prototype.resume = function () {
    if (this.started && this.visible) this.svg.classList.add('is-live');
    if (this.running || !this.started || reduceMotion.matches || !this.visible) return;
    this.running = true;
    var self = this;
    var tick = function (now) {
      if (!self.running) return;
      var elapsed = now - self.t0;
      self.render(elapsed / 1000, null, elapsed);
      self.emit();
      self.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  };

  Wave.prototype.pause = function () {
    // Off screen: the CSS glows stop too (animation-play-state follows .is-live).
    if (!reduceMotion.matches) this.svg.classList.remove('is-live');
    this.running = false;
    if (this.frame) cancelAnimationFrame(this.frame);
  };

  var observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var wave = entry.target.cWave;
          if (!wave) return;
          wave.visible = entry.isIntersecting;
          if (entry.isIntersecting) {
            if (wave.spec.start !== 'manual') wave.start();
            wave.resume();
          } else {
            wave.pause();
          }
        });
      }, { rootMargin: '0px 0px -15% 0px' })
    : null;

  function mount(root) {
    (root || document).querySelectorAll('svg[data-c-wave]').forEach(function (svg) {
      if (svg.cWave) return;
      var wave;
      try {
        wave = new Wave(svg);
      } catch (error) {
        // One bad spec must not stop the other graphics on the page from drawing.
        console.error('c-home-waves: could not draw', svg, error);
        return;
      }
      if (observer) observer.observe(svg);
      else { wave.visible = true; wave.start(); }
    });
  }

  reduceMotion.addEventListener && reduceMotion.addEventListener('change', function () {
    document.querySelectorAll('svg[data-c-wave]').forEach(function (svg) {
      var wave = svg.cWave;
      if (!wave) return;
      if (reduceMotion.matches) { wave.pause(); wave.render(0, 1); wave.emit(); }
      else if (wave.started) { wave.t0 = performance.now() - 60000; wave.resume(); }
    });
  });

  window.CHomeWaves = { mount: mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { mount(); });
  else mount();
  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', function (event) { mount(event.target); });
})();
